import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import BrandLogo from "@/components/BrandLogo";
import { Loader2, AlertCircle } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Read session_id from URL fragment (or query as fallback)
    const hash = window.location.hash || "";
    const hashParams = new URLSearchParams(hash.replace(/^#/, ""));
    const queryParams = new URLSearchParams(location.search);
    const sessionId = hashParams.get("session_id") || queryParams.get("session_id");
    // Optional: which provider - defaults to emergent_google
    const provider = hashParams.get("provider") || queryParams.get("provider") || "emergent_google";

    if (!sessionId) {
      setError("The sign-in link was empty. Please try Continue with Google again.");
      setTimeout(() => navigate("/login?error=oauth_empty", { replace: true }), 1200);
      return;
    }

    (async () => {
      try {
        const { data } = await api.post(`/auth/oauth/${provider}/exchange`, {
          session_id: sessionId,
        });
        setUser(data);
        // Clean the URL fragment and route into the app
        window.history.replaceState({}, document.title, "/app/dashboard");
        navigate("/app/dashboard", { replace: true });
      } catch (e) {
        const msg =
          e?.response?.data?.detail ||
          "We couldn't complete sign-in. Please try again.";
        setError(typeof msg === "string" ? msg : "Sign-in failed. Please try again.");
        setTimeout(() => navigate(`/login?error=oauth`, { replace: true }), 1800);
      }
    })();
  }, [navigate, location.search, setUser]);

  return (
    <div
      data-testid="auth-callback-loading"
      className="min-h-screen flex items-center justify-center relative"
    >
      <div className="ambient-bg" />
      <div className="relative flex flex-col items-center gap-6">
        <BrandLogo variant="icon" iconSize={44} as="div" />
        {error ? (
          <div className="flex items-center gap-2 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/25 rounded-lg px-4 py-3 max-w-md text-center">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-zinc-400 text-sm tracking-wide">
            <Loader2 className="h-4 w-4 animate-spin text-amber-400" />
            Signing you in…
          </div>
        )}
      </div>
    </div>
  );
}
